import { Col, Pagination } from "react-bootstrap";
import React, { useMemo } from "react";
import { ParamKeyValuePair, useSearchParams } from "react-router-dom";

type Props = {
    count: number;
    next: string | null;
    previous: string | null;
};

const ListPagination = ({ count, next, previous }: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Memoize entries to avoid recalculating on every render
    const entries = useMemo(() => {
        const entries: ParamKeyValuePair[] = [];
        searchParams.forEach((value, key) => {
            if (key !== 'page') {
                entries.push([key, value]);
            }
        });
        return entries;
    }, [searchParams]);

    // Page handler function
    const handlePageChange = (page: number) => {
        setSearchParams([...entries, ['page', page.toString()]]);
    };

    // Memoize calculations
    const totalPages = useMemo(() => Math.ceil(count / 25), [count]);
    const currentPage = useMemo(() => Math.max(1, parseInt(searchParams.get('page') || '1')), [searchParams]);

    // Page boundary checks
    const hasNext = useMemo(() => next !== null, [next]);
    const hasPrevious = useMemo(() => previous !== null, [previous]);

    return (
        <Col className='text-center'>
            <Pagination className='justify-content-center'>
                <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={!hasPrevious}
                />
                <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrevious}
                />
                {Array.from({ length: totalPages }, (_, page) => (
                    <Pagination.Item
                        key={page + 1}
                        active={page + 1 === currentPage}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        {page + 1}
                    </Pagination.Item>
                ))}
                <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNext}
                />
                <Pagination.Last
                    onClick={() => handlePageChange(totalPages)}
                    disabled={!hasNext}
                />
            </Pagination>
        </Col>
    );
};

export default ListPagination;